import { useEffect, useState } from 'react';
import { Box, FormHelperText, Grid, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { gridLayout } from '../SectionLayout';
import { Price } from '..';
import {
  checkPasswordFeatureInConflict,
  getPropertyValue,
  getFeatureTooltip,
} from '../../services/ConfigurationDataHelperFunctions';
import { AppStore } from '../../store';
import { Asterisk, EConfigitProperty, EVariableType } from '../../data/Constants';
import { hasDefaultValue } from '../../services/VariableAssignmentsTableHelperFunctions';
import { DefaultIcon } from '../DefaultIcon';

import type { IStringControlProps } from '../../../types';

/**
 * This function generate the asterisx value based on MLE property value
 * @param {number} maxLength the MLE Property value
 * @return {string} returns the asterisx value
 */
function generateMaskValue(maxLength: number): string {
  const minLength = 10;
  if (maxLength < minLength) {
    maxLength = minLength;
  }
  return Asterisk.repeat(maxLength + 2);
}

/**
 * Renders an input box for strings.
 * Used for variables which allow the user to set a custom string
 * @param {IStringControlProps} props the properties for the string input component
 * @returns {JSX.Element} the string input component
 */
export const StringControl = ({
  label,
  value,
  onChange,
  disabled,
  variable,
}: IStringControlProps) => {
  const [internalValue, setInternalValue] = useState(value?.trim() || '');
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const { t } = useTranslation();
  const maxValidation = variable?.pdmProperties?.maxLength ?? null;
  const maxValue = Number(maxValidation);
  const placeholderMsg = t('errorMessages.maxLength', { length: maxValue });
  const configuration = AppStore.getState().configuration;
  const title = getFeatureTooltip(variable, disabled);

  //Masking the  feature value to ******** if the IsPassword variable in Conflict removed assignments
  const hasPasswordFeatureConflict =
    checkPasswordFeatureInConflict(configuration)?.variable?.id === variable.id;

  useEffect(() => {
    setInternalValue(value?.trim() || '');
  }, [value]);

  //Validate string to check if it exceeds the Max. length
  const validateUserString = (userInput: string) => {
    if (userInput.length > maxValue) {
      const errors = [];
      errors.push(t('errorMessages.maxLength', { length: maxValue }));
      setErrorMessage(errors);
    } else {
      setErrorMessage([]);
    }
  };

  //Update the current user value to the local state
  const onUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateUserString(e.target.value);
    const currentValue = e.target.value.substring(0, maxValue);

    setInternalValue(currentValue);
  };

  // To call Congifure onBlur of the FormField
  const onEventChange = () => {
    //Clear error messages on Configure call since it has acceptable length
    setErrorMessage([]);

    //Restrict multiple spaces and allow sigle spaces for password feature
    //trimming spaces before and after the value
    const isPasswordFeature = getPropertyValue<boolean>(
      variable,
      EConfigitProperty.IsPassword,
      EVariableType.Boolean
    );
    let trimValue = internalValue;
    if (isPasswordFeature && trimValue !== ' ') {
      trimValue = trimValue.trim();
      setInternalValue(trimValue);
    }

    // to prevent Configure
    if (trimValue !== value && onChange) {
      onChange(trimValue);
    }
  };

  return (
    <Grid container sx={{ alignItems: 'stretch' }} size={gridLayout.fullWidth}>
      <Grid
        className="feature-label-grid"
        size={{
          xs: gridLayout.xs.input,
          sm: gridLayout.sm.input,
        }}
      >
        <DefaultIcon show={!!hasDefaultValue(variable)} />
        <Tooltip
          title={title?.length > 0 ? <span style={{ whiteSpace: 'pre-line' }}>{title}</span> : null}
        >
          <Box style={{ display: 'inline-block' }}>
            <TextField
              className="w-25ch"
              key={label}
              color="primary"
              type="text"
              value={
                hasPasswordFeatureConflict
                  ? generateMaskValue(maxValue)
                  : internalValue.substring(0, maxValue)
              }
              onChange={(e) => onUpdate(e)}
              onBlur={() => onEventChange()}
              disabled={disabled}
              data-testid={variable.id}
              placeholder={placeholderMsg}
              sx={disabled ? { pointerEvents: 'none' } : {}}
            />
          </Box>
        </Tooltip>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'center' }}
        size={{
          xs: gridLayout.xs.infoIcon,
          sm: gridLayout.sm.infoIcon,
        }}
      />
      <Grid
        size={{
          xs: gridLayout.xs.qty,
          sm: gridLayout.sm.qty,
        }}
      ></Grid>
      <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'center' }}
        size={{
          xs: gridLayout.xs.optional,
          sm: gridLayout.sm.optional,
        }}
      >
        {/* Optional check box should not be available for string variables */}
        <Box className="hyphen"> - </Box>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'right', justifyContent: 'right' }}
        size={{
          xs: gridLayout.xs.price,
          sm: gridLayout.sm.price,
        }}
      >
        <Box sx={{ display: { xs: 'inline', sm: 'none' }, textAlign: 'center' }}>
          {t('labels.listPrice')}:&nbsp;
        </Box>
        <Price price={variable.price} isBundled={variable.bundleInfo?.isBundled} />
      </Grid>
      <FormHelperText className="validation-error">
        {errorMessage.map((error: string, idx: number) => (
          <span key={idx + variable.id}>{error}</span>
        ))}
      </FormHelperText>
    </Grid>
  );
};
